import React, { useCallback } from 'react';

import type { Row, HeaderGroup } from 'react-table';

import GroupedRow from '../table-components/grouped-row';
import { TableRow } from '../table-components/';
import type { RoutesTableTranslations } from '../../routes-planner/translations';
import useStore from '../../../stores/helpers/use-context';
import { isRouteExistsAndUnassigned, isTaskAssigned } from '../../routes-planner/utils';
import type { RoutesPlannerTask } from '../../../types';

const GHOST_HEIGHT = 40;
const GHOST_WIDTH = 18;

const getGhostElement = (row: Row<RoutesPlannerTask>) => {
	const div = document.createElement('div');
	div.style.width = `${GHOST_WIDTH}px`;
	div.style.height = `${GHOST_HEIGHT}px`;
	div.style.borderRadius = '2px';
	div.style.position = 'absolute';
	div.style.left = '-999999px';
	div.style.backgroundColor = row.values.colorMark;

	document.body.appendChild(div);

	setTimeout(() => {
		document.body.removeChild(div);
	}, 0);

	return div;
};

const isSomeRouteGroupsExpanded = (expanded: Record<string, boolean>) =>
	Object.keys(expanded).some(rowId => rowId.includes('>'));

const getMinWidthProp = (expanded: Record<string, boolean>, totalColumnsWidth: number): string | number => {
	if (isSomeRouteGroupsExpanded(expanded)) {
		return totalColumnsWidth;
	}
	return 'auto';
};

const useRenderRow = (
	rows: Row<RoutesPlannerTask>[],
	prepareRow: (row: Row<RoutesPlannerTask>) => void,
	totalColumnsWidth: number,
	expanded: Record<string, boolean>,
	onToggleRuns: (runsIds: number[], show: boolean) => void,
	handleGroupExpand: (rowIndex: number) => void,
	headerGroups: HeaderGroup<RoutesPlannerTask>[],
	onDragStart: (id: number) => void,
	onDragEnd: () => void,
	translations: RoutesTableTranslations,
	onMouseEnter?: (rowId: number) => void,
	onMouseLeave?: (rowId: number) => void
) => {
	const { routesById, optimizationType, isReadOnly } = useStore();

	return useCallback(
		({ index, style }) => {
			const row = rows[index];
			prepareRow(row);

			if (row.isGrouped) {
				return (
					<GroupedRow
						row={row}
						style={{ ...style, minWidth: getMinWidthProp(expanded, totalColumnsWidth) }}
						rowIndex={index}
						onSelect={onToggleRuns}
						onExpand={() => handleGroupExpand(index)}
					/>
				);
			}

			const shouldRenderHeader = rows[index - 1]?.isGrouped;

			const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
				event.dataTransfer.effectAllowed = 'copy';
				const objectItem = {
					content: 'content',
					target: 'item',
					task_id: row.original.id
				};
				event.dataTransfer.setData('text', JSON.stringify(objectItem));

				const ghostElement = getGhostElement(row);

				event.dataTransfer.setDragImage(ghostElement, GHOST_WIDTH / 2, GHOST_HEIGHT / 2);
				onDragStart(row.original.id);
			};

			const runId = row.values.run_id;

			return (
				<TableRow
					row={row}
					headerGroups={headerGroups}
					shouldRenderHeader={shouldRenderHeader}
					translations={translations}
					onDragStart={!isTaskAssigned(row.original.run_id) && !isReadOnly ? handleDragStart : undefined}
					onDragEnd={onDragEnd}
					onMouseEnter={() => onMouseEnter?.(row.original.id)}
					onMouseLeave={() => onMouseLeave?.(row.original.id)}
					{...row.getRowProps({ style: { ...style, minWidth: totalColumnsWidth } })}
					isLocked={isRouteExistsAndUnassigned(routesById[runId], optimizationType)}
				/>
			);
		},
		[rows, prepareRow, expanded, onToggleRuns, totalColumnsWidth]
	);
};

export default useRenderRow;
